import { SparklesIcon } from "@heroicons/react/24/solid";

import { clsxMerge } from "shared/lib/helpers";
import CircularLoading from "@/components/base/circular-loading";
import { BorderWithGradient } from "shared/ui/border-with-gradient";
import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";

export const GenereateAIEmailButton = ({
  className,
}: {
  className?: string;
}) => {
  const { handleComposeWithAI, isAIGenerationStarted } =
    useEmailTemplatesContext();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(isAIGenerationStarted);
  }, [isAIGenerationStarted]);

  const handleOnPrompttBlurEffect = () => {
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const throttledOnPrompttBlurEffect = useCallback(
    debounce(handleOnPrompttBlurEffect, 5000, {
      leading: true,
      trailing: false,
    }),
    []
  );

  const handleOnClick = () => {
    handleComposeWithAI();
  };

  useEffect(() => {
    const handleOnBlur = () => {
      throttledOnPrompttBlurEffect();
    };

    window.document.addEventListener(
      CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.PROMT_BLUR,
      handleOnBlur
    );

    return () => {
      window.document.removeEventListener(
        CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.PROMT_BLUR,
        handleOnBlur
      );
    };
  }, []);

  return (
    <BorderWithGradient
      className="group rounded-[5px] px-2 py-0"
      containerClassName="rounded-md p-[1px]"
    >
      <button
        type="button"
        className={clsxMerge(
          "transition-all duration-300",
          "btn-ae border-none typography-header-8-semibold",
          "flex-nowrap whitespace-nowrap",
          /**
           * daisyUI gives `.btn` a neutral background-color. It never showed
           * before because the button also carried `bg-clip-text`, which
           * clipped that fill away along with everything else - the
           * see-through button was a side effect of the gradient hack, not a
           * choice. With the clip gone the fill appears, so transparency is
           * asked for outright now, in every state daisyUI repaints. The white
           * behind it comes from BorderWithGradient's inner div.
           */
          "bg-transparent hover:bg-transparent focus:bg-transparent",
          "active:bg-transparent disabled:bg-transparent",
          isLoading && "opacity-80",
          className
        )}
        disabled={isLoading}
        onClick={handleOnClick}
      >
        {isLoading ? (
          <CircularLoading className="mr-2 w-4 text-[#714CC9] transition-colors duration-300 group-hover:text-[#CC3997]" />
        ) : (
          <SparklesIcon
            className={clsxMerge(
              "transition-colors duration-300",
              "mb-1 mr-2 w-4",
              "text-[#714CC9] group-hover:text-[#CC3997]"
            )}
          />
        )}
        {/*
          The gradient belongs on the label, not on the button.
          `text-gradient` is `bg-clip-text` plus `text-transparent`, so
          whichever element carries it has its own background clipped to the
          glyphs - and the button is a `.btn`, which daisyUI gives a
          background-color and a `--tw-bg-opacity` it drops to 0.2 while
          disabled. This button disables itself for 500ms after every prompt
          blur, so the label kept washing out to near-invisible. A plain span
          is subject to none of that.

          One layer only, and it does not move. Carrying `text-gradient` on
          both the button and the label meant two clipped copies of the same
          glyphs, and `group-hover:translate-x-2` slid one of them out from
          under the other - the doubled letters. Hover now lives where it
          cannot tear: the wrapper reverses its border, the icon shifts
          colour, and the text stays put.
        */}
        <span className="text-gradient">Compose with AI</span>
      </button>
    </BorderWithGradient>
  );
};
